// ScientificNursingCarePromotionAPI
import { AxiosInstance } from 'axios';
import { createAxiosInstance } from '../constants/AWS';
import { apiRequestFunc } from '../utils/apiUtil';
import _ from 'lodash';

// アクションタイプの定義
export const ScientificNursingCarePromotionAPIActionType = {
  LOADING: 'SCIENTIFIC_NURSING_CARE_PROMOTION_API_LOADING',
  SUCCESS: 'SCIENTIFIC_NURSING_CARE_PROMOTION_API_SUCCESS',
  ERROR: 'SCIENTIFIC_NURSING_CARE_PROMOTION_API_ERROR',
};

const API_VERSION = 'v2';

export class ScientificNursingCarePromotionAPI {
  /** インスタンス */
  private static _instance: ScientificNursingCarePromotionAPI;
  private axiosInstance!: AxiosInstance;

  // .envの設定
  private API_BASE_URL = String(process.env.REACT_APP_MOFF_API_URL);

  /** インスタンスの取得 */
  public static get instance(): ScientificNursingCarePromotionAPI | null {
    // _inctanceが存在しない場合に、new ScientificNursingCarePromotionAPI()を実行する。
    if (!this._instance) {
      this._instance = new ScientificNursingCarePromotionAPI();
      // Cognito JWTでの認証はcreateAxiosInstance内のrequest interceptorで毎回最新トークンを付与する.
      const axiosInstance = createAxiosInstance(this._instance.API_BASE_URL, null, null);

      if (axiosInstance) {
        this._instance.axiosInstance = axiosInstance;
      } else {
        return null;
      }
    }

    // 生成済みのインスタンスを返す
    return this._instance;
  }

  // 後方互換のため残置。JWTはrequest interceptorで毎リクエスト時に自動付与される (Issue #842).
  public async setAxiosInstanceWithJwtToken(): Promise<void> {
    return;
  }

  // 評価一覧取得API [APIリクエスト]
  public getEvaluationsAPI(userId: string) {
    const uniqueLogicFunc: any = async () => {
      const response = await this.getEvaluationsFunc(userId);
      return response.data.data || [];
    };
    return apiRequestFunc(
      ScientificNursingCarePromotionAPIActionType.LOADING,
      ScientificNursingCarePromotionAPIActionType.SUCCESS,
      ScientificNursingCarePromotionAPIActionType.ERROR,
      uniqueLogicFunc,
    );
  }

  // 評価詳細取得API [APIリクエスト]
  public getEvaluationAPI(userId: string, evaluationId: string) {
    const uniqueLogicFunc: any = async () => {
      const response = await this.getEvaluationFunc(userId, evaluationId);
      return response.data;
    };
    return apiRequestFunc(
      ScientificNursingCarePromotionAPIActionType.LOADING,
      ScientificNursingCarePromotionAPIActionType.SUCCESS,
      ScientificNursingCarePromotionAPIActionType.ERROR,
      uniqueLogicFunc,
    );
  }

  // 評価新規作成API [APIリクエスト]
  public createEvaluationAPI(userId: string, params: any) {
    const uniqueLogicFunc: any = async () => {
      const response = await this.createEvaluationFunc(userId, params);
      return _.zipObject(['evaluation', 'is_created'], [response.data, true]);
    };
    return apiRequestFunc(
      ScientificNursingCarePromotionAPIActionType.LOADING,
      ScientificNursingCarePromotionAPIActionType.SUCCESS,
      ScientificNursingCarePromotionAPIActionType.ERROR,
      uniqueLogicFunc,
    );
  }

  // 評価更新API [APIリクエスト]
  public updateEvaluationAPI(userId: string, evaluationId: string, params: any) {
    const uniqueLogicFunc: any = async () => {
      const response = await this.updateEvaluationFunc(userId, evaluationId, params);
      return _.zipObject(['evaluation', 'is_updated'], [response.data, true]);
    };
    return apiRequestFunc(
      ScientificNursingCarePromotionAPIActionType.LOADING,
      ScientificNursingCarePromotionAPIActionType.SUCCESS,
      ScientificNursingCarePromotionAPIActionType.ERROR,
      uniqueLogicFunc,
    );
  }

  // 評価削除API [APIリクエスト]
  public deleteEvaluationAPI(userId: string, evaluationId: string) {
    const uniqueLogicFunc: any = async () => {
      const response = await this.deleteEvaluationFunc(userId, evaluationId);
      return _.zipObject(['evaluation', 'is_deleted'], [response.data, true]);
    };
    return apiRequestFunc(
      ScientificNursingCarePromotionAPIActionType.LOADING,
      ScientificNursingCarePromotionAPIActionType.SUCCESS,
      ScientificNursingCarePromotionAPIActionType.ERROR,
      uniqueLogicFunc,
    );
  }

  // 評価一覧取得関数 [APIリクエストから呼び出し]
  public async getEvaluationsFunc(userId: string) {
    return this.axiosInstance.get(`${API_VERSION}/scientific_nursing_care_promotion/${userId}`);
  }

  // 期間指定で評価一覧取得関数 [APIリクエストから呼び出し]
  public async getEvaluationsByDateRangeFunc(userId: string, fromDate: string, toDate: string) {
    return this.axiosInstance.get(
      `${API_VERSION}/scientific_nursing_care_promotion/${userId}/from/${fromDate}/to/${toDate}`,
    );
  }

  // 指定月のデータを取得するAPI [APIリクエスト]
  public getRecordByMonthAPI(uniqueIds: any[], month: string) {
    const uniqueLogicFunc: any = async () => {
      try {
        // JWT tokenを設定
        await this.setAxiosInstanceWithJwtToken();

        // 月の開始日と終了日を取得
        const fromDate = month ? `${month}-01` : '';
        const lastDay = month
          ? new Date(parseInt(month.split('-')[0]), parseInt(month.split('-')[1]), 0).getDate()
          : 31;
        const toDate = month ? `${month}-${lastDay}` : '';

        // 各ユーザーの科学的介護データを取得
        const usersWithData: { [key: string]: boolean } = {};

        await Promise.all(
          uniqueIds.map(async (uniqueId) => {
            try {
              // 科学的介護推進に関する評価データを期間指定で取得
              const response = await this.getEvaluationsByDateRangeFunc(uniqueId.app_user_id, fromDate, toDate);
              const evaluations = response.data.data || [];

              // データが存在する場合は、そのユーザーを表示対象とする
              if (evaluations && Array.isArray(evaluations) && evaluations.length > 0) {
                usersWithData[uniqueId.app_user_id] = true;
              }
            } catch (error) {
              console.error(`Error fetching scientific nursing data for user ${uniqueId.app_user_id}:`, error);
            }
          }),
        );

        return { usersWithData };
      } catch (error) {
        console.error('Error fetching scientific nursing data:', error);
        throw error;
      }
    };

    return apiRequestFunc(
      ScientificNursingCarePromotionAPIActionType.LOADING,
      ScientificNursingCarePromotionAPIActionType.SUCCESS,
      ScientificNursingCarePromotionAPIActionType.ERROR,
      uniqueLogicFunc,
    );
  }

  // 評価詳細取得関数 [APIリクエストから呼び出し]
  public async getEvaluationFunc(userId: string, evaluationId: string) {
    return this.axiosInstance.get(`${API_VERSION}/scientific_nursing_care_promotion/${userId}/form/${evaluationId}`);
  }

  // 評価新規作成関数 [APIリクエストから呼び出し]
  public async createEvaluationFunc(userId: string, params: any) {
    return this.axiosInstance.post(`${API_VERSION}/scientific_nursing_care_promotion/${userId}`, params);
  }

  // 評価更新関数 [APIリクエストから呼び出し]
  public async updateEvaluationFunc(userId: string, evaluationId: string, params: any) {
    return this.axiosInstance.put(
      `${API_VERSION}/scientific_nursing_care_promotion/${userId}/form/${evaluationId}`,
      params,
    );
  }

  // 評価削除関数 [APIリクエストから呼び出し]
  public async deleteEvaluationFunc(userId: string, evaluationId: string) {
    return this.axiosInstance.delete(`${API_VERSION}/scientific_nursing_care_promotion/${userId}/form/${evaluationId}`);
  }
}

export const createScientificNursingCarePromotionAPIInstance = () => {
  // インスタンスが存在しない場合、エラーアクションを返す
  return ScientificNursingCarePromotionAPI.instance === null ? null : ScientificNursingCarePromotionAPI.instance;
};
